// src/components/matricula/FormMatricula.tsx

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { classNames } from 'primereact/utils';
import { InputMask } from 'primereact/inputmask';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { FileUpload } from 'primereact/fileupload';
import { confirmDialog } from 'primereact/confirmdialog';
import { Printd } from 'printd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../api/api';
import { cpf } from 'cpf-cnpj-validator';
import logo from '../assets/logo_conservatorio.jpg';

import '../layout/sass/_form.scss';

export const FormMatricula = () => {
    const toast = useRef(null);
    const fileUploadRef = useRef(null);
    const printd = new Printd();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [perfil, setPerfil] = useState(null);
    const [listTurmaTeoria, setListTurmaTeoria] = useState([]);
    const [listTurmaInstrumento, setListTurmaInstrumento] = useState([]);
    const [listCursoInstrumentoEspera, setListCursoInstrumentoEspera] = useState([]);
    const [turmaTeoriaSelected, setTurmaTeoriaSelected] = useState(null);
    const [turmaInstrumentoSelected, setTurmaInstrumentoSelected] = useState(null);
    const [showComprovante, setShowComprovante] = useState(false);
    const [matriculaGerada, setMatriculaGerada] = useState(null);
    const [disableUpload, setDisableUpload] = useState(true);

    const [showSemInscricaoSorteada, setShowSemInscricaoSorteada] = useState(false);
    const [showJaMatriculado, setShowJaMatriculado] = useState(false);
    const [showSemPerfil, setShowSemPerfil] = useState(false);
    const [showTurmaCheia, setShowTurmaCheia] = useState(false);

    const anoAtual = new Date().getFullYear();
    const notaCorte = 60;

    const [dadosComprovante, setDadosComprovante] = useState(null);

    const cssPrint = `
    #print-area { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
    .header { text-align: center; margin-bottom: 20px; }
    .field { margin: 12px 0; font-size: 1.05rem; }
    img { max-width: 220px; }
    h3 { color: #2c3e50; }
  `;

    const defaultValues = {
        cpf: '',
        alunoId: '',
        doc: '',
    };

    const validationSchema = Yup.object().shape({
        alunoId: Yup.number().required('O aluno é obrigatório'),
        cpf: Yup.string().required('O CPF é obrigatório'),
    });

    const formik = useFormik({
        initialValues: defaultValues,
        validationSchema,
        onSubmit: async (values) => {
            if (!perfil) return;

            setIsSubmitting(true);

            try {
                // Sempre cria apenas UMA matrícula (teoria + instrumento opcional)
                const matriculaPayload = {
                    turmaId: turmaTeoriaSelected,               // Obrigatório (teoria)
                    alunoId: perfil.id,
                    status: 'MATRICULADO',
                    dataMatricula: new Date(),
                    conclusao: `${anoAtual}-12-31`,
                    notaFinal: 0,
                };

                // Se selecionou instrumento da lista de espera → adiciona instrumento_id
                if (turmaInstrumentoSelected) {
                    matriculaPayload.instrumentoId = turmaInstrumentoSelected;  // ID do CURSO
                }

                // Verificação de lotação (só para teoria, que tem turma_id)
                if (turmaTeoriaSelected) {
                    const turmaRes = await api.get(`/turma/${turmaTeoriaSelected}`);
                    const inscritosRes = await api.get(`/matricula/inscritos/${turmaTeoriaSelected}`);

                    const qtdeInscritos = Number(inscritosRes?.data ?? 0);
                    const qtdeVagas = Number(turmaRes?.data?.qtdeVagas ?? 0);

                    console.log(`[DEBUG] Turma teoria ${turmaTeoriaSelected}: inscritos=${qtdeInscritos}, vagas=${qtdeVagas}`);

                    if (qtdeInscritos >= qtdeVagas) {
                        toast.current?.show({
                            severity: 'error',
                            summary: 'Turma lotada',
                            detail: `A turma de teoria selecionada (${turmaRes.data?.perfilNome || '—'}) está cheia.`,
                            life: 6000,
                        });
                        setShowTurmaCheia(true);
                        return;  // Aborta o submit inteiro se teoria lotada
                    }
                }

                // Cria a única matrícula
                await api.post('/matricula', matriculaPayload);

                // Busca a matrícula gerada para o comprovante
                const matriculasRes = await api.get(`/matricula/cpf/${perfil.cpf}`);
                if (matriculasRes.data?.length > 0) {
                    const ultima = matriculasRes.data.sort((a, b) =>
                        new Date(b.dataMatricula) - new Date(a.dataMatricula)
                    )[0];
                    setMatriculaGerada(ultima);

                    setDadosComprovante({
                        nome: perfil?.nome || '—',
                        cpf: perfil?.cpf || '—',
                        data: new Date().toLocaleString('pt-BR'),
                        codigo: ultima?.id || '—',
                        turmaTeoria: turmaTeoriaSelected
                            ? listTurmaTeoria.find(t => t.value === turmaTeoriaSelected)?.label || '—'
                            : '—',
                        instrumento: turmaInstrumentoSelected
                            ? listCursoInstrumentoEspera.find(i => i.id === turmaInstrumentoSelected)?.nome || '—'
                            : '—',
                    });

                    confirmDialog({
                        message: 'Matrícula realizada com sucesso! Deseja imprimir o comprovante?',
                        header: 'Matrícula Concluída',
                        acceptLabel: 'Sim',
                        rejectLabel: 'Não',
                        accept: () => setShowComprovante(true),
                    });
                }

                // Limpeza final
                formik.resetForm();
                fileUploadRef.current?.clear();
                setPerfil(null);
                setListTurmaTeoria([]);
                setListCursoInstrumentoEspera([]);
                setTurmaTeoriaSelected(null);
                setTurmaInstrumentoSelected(null);

            } catch (err) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Erro ao matricular',
                    detail: err?.response?.data?.message || err.message,
                });
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const isInvalid = (field) => !!(formik.touched[field] && formik.errors[field]);

    const handleCpfComplete = useCallback(async (e) => {
        const cpfValue = e.value.trim();
        if (!cpf.isValid(cpfValue)) {
            toast.current?.show({ severity: 'warn', summary: 'CPF inválido' });
            return;
        }

        try {
            // 1. Busca perfil
            const perfilRes = await api.get(`/perfil/cpf/${cpfValue}`);
            if (!perfilRes?.data) {
                setShowSemPerfil(true);
                return;
            }
            setPerfil(perfilRes.data);
            formik.setFieldValue('alunoId', perfilRes.data.id);
            formik.setFieldValue('cpf', cpfValue);

            // 2. Busca matrículas existentes
            const matriculasRes = await api.get(`/matricula/cpf/${cpfValue}`);
            const matriculas = matriculasRes.data || [];

            // 3. Tem matrícula no ano atual? → já matriculado
            const matAnoAtual = matriculas.filter(m => m.turma?.ano === anoAtual);
            if (matAnoAtual.length > 0) {
                setShowJaMatriculado(true);
                setPerfil(null);
                return;
            }

            // 4. Tem matrícula no ano anterior? → não deve usar este form
            const matAnoAnterior = matriculas.filter(m => m.turma?.ano === anoAtual - 1);
            if (matAnoAnterior.length > 0) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Rematrícula necessária',
                    detail: 'Este aluno possui matrícula anterior. Use o formulário de rematrícula.',
                });
                setPerfil(null);
                return;
            }

            // 5. Aluno novo → verifica se tem inscrição sorteada
            let inscricoes = [];
            try {
                const inscRes = await api.get(`/inscricoes/porCpf/${cpfValue}`); // ajuste endpoint se necessário
                // ou `/inscricao/ultima/${perfilRes.data.id}`
                inscricoes = inscRes.data || [];
            } catch (err) {
                console.warn('Erro ao buscar inscrições', err);
            }

            const ultimaInscricao = inscricoes.length > 0 ? inscricoes[0] : null;

            if (!ultimaInscricao?.sorteado) {
                setShowSemInscricaoSorteada(true);
                setPerfil(null);
                return;
            }

            // 6. Aluno elegível para matrícula nova → carrega turmas por idade
            const idade = calcularIdade(perfilRes.data.dataNascimento);
            const turmasRes = await api.get(`/turma/comVagasPorIdade/${idade}/${anoAtual}`);
            setListTurmaTeoria(
                turmasRes.data.map(t => ({
                    label: `${t.perfilNome || '—'} - ${t.cursoNome} - ${t.diaSemana} ${t.hora}`,
                    value: t.id,
                }))
            );

            // Instrumento: lista de espera
            const esperaRes = await api.get(`/curso/instrumentoEspera`);
            setListCursoInstrumentoEspera(esperaRes.data || []);

            setDisableUpload(false);

        } catch (err) {
            setShowSemPerfil(true);
            toast.current?.show({
                severity: 'error',
                summary: 'Erro ao validar matrícula',
                detail: err.message,
            });
        }
    }, [formik]);

    const calcularIdade = (dataNascimento) => {
        const hoje = new Date();
        const nascimento = new Date(dataNascimento);
        let idade = hoje.getFullYear() - nascimento.getFullYear();
        const mesAtual = hoje.getMonth();
        const mesNasc = nascimento.getMonth();
        if (mesAtual < mesNasc || (mesAtual === mesNasc && hoje.getDate() < nascimento.getDate())) {
            idade--;
        }
        return idade;
    };

    const handleUpload = (event) => {
        if (!event.files?.[0] || !perfil?.cpf) return;

        const file = event.files[0];
        const extensao = file.name.split('.').pop();
        const renamedFile = new File([file], `${perfil.cpf}_doc.${extensao}`);

        const formData = new FormData();
        formData.append('doc', renamedFile);
        formData.append('cpf', perfil.cpf);
        formData.append('nomeAluno', perfil.nome);

        api.post('/matricula/uploadDoc', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }).catch(err => {
            toast.current?.show({ severity: 'error', summary: 'Erro no upload', detail: err.message });
        });
    };

    const handlePrint = () => {
        const el = document.getElementById('comprovante-matricula');
        if (el) printd.print(el, [cssPrint]);
    };

    return (
        <div className="card">
            <Toast ref={toast} position="bottom-right" />

            <h2>FORMULÁRIO DE MATRÍCULA – {anoAtual}</h2>
            <p>Preencha o CPF para iniciar o processo de matrícula nova.</p>

            <form onSubmit={formik.handleSubmit} className="p-fluid p-mt-4">
                <div className="p-field">
                    <label htmlFor="cpf">CPF *</label>
                    <InputMask
                        id="cpf"
                        mask="999.999.999-99"
                        unmask
                        value={formik.values.cpf}
                        onChange={formik.handleChange}
                        onComplete={handleCpfComplete}
                        className={classNames({ 'p-invalid': isInvalid('cpf') })}
                    />
                </div>

                <div className="p-field">
                    <label>Aluno</label>
                    <input
                        type="text"
                        value={perfil?.nome || ''}
                        disabled
                        className="p-inputtext p-component p-disabled"
                    />
                </div>

                {listTurmaTeoria.length > 0 && (
                    <div className="p-field">
                        <label>Turma de Teoria</label>
                        <Dropdown
                            value={turmaTeoriaSelected}
                            options={listTurmaTeoria}
                            onChange={(e) => setTurmaTeoriaSelected(e.value)}
                            placeholder="Selecione a turma de teoria"
                            filter
                            showClear
                        />
                    </div>
                )}

                {listCursoInstrumentoEspera.length > 0 && (
                    <div className="p-field">
                        <label>Instrumento (lista de espera)</label>
                        <Dropdown
                            value={turmaInstrumentoSelected}
                            options={listCursoInstrumentoEspera}
                            optionLabel="nome"
                            optionValue="id"
                            onChange={(e) => setTurmaInstrumentoSelected(e.value)}
                            placeholder="Selecione o instrumento"
                            filter
                            showClear
                        />
                    </div>
                )}

                <div className="p-field p-mt-4">
                    <label>Anexar documento de identidade com CPF</label>
                    <FileUpload
                        ref={fileUploadRef}
                        name="doc"
                        disabled={disableUpload}
                        auto
                        customUpload
                        uploadHandler={handleUpload}
                        accept="image/*,application/pdf"
                        maxFileSize={2000000}
                        chooseLabel="Selecionar documento"
                        uploadLabel="Enviar"
                        cancelLabel="Cancelar"
                        onSelect={() => setDisableUpload(false)}
                        onClear={() => setDisableUpload(true)}
                    />
                </div>

                <div className="p-mt-6">
                    <Button
                        type="submit"
                        label="Confirmar Matrícula"
                        icon="pi pi-check"
                        loading={isSubmitting}
                        disabled={isSubmitting || !perfil || (!turmaTeoriaSelected && !turmaInstrumentoSelected)}
                        className="p-button-success"
                    />
                </div>
            </form>

            {/* Diálogos */}
            <Dialog visible={showSemInscricaoSorteada} header="Atenção" onHide={() => setShowSemInscricaoSorteada(false)}>
                <p>Não foi encontrada inscrição sorteada para este CPF.</p>
                <p>A matrícula nova só é permitida para alunos com inscrição sorteada na campanha vigente.</p>
            </Dialog>

            <Dialog visible={showJaMatriculado} header="Atenção" onHide={() => setShowJaMatriculado(false)}>
                <p>Já existe matrícula registrada para este CPF no ano atual.</p>
            </Dialog>

            <Dialog visible={showSemPerfil} header="Atenção" onHide={() => setShowSemPerfil(false)}>
                <p>Perfil não encontrado para este CPF.</p>
                <p>Procure a administração do conservatório.</p>
            </Dialog>

            <Dialog visible={showTurmaCheia} header="Turma lotada" onHide={() => setShowTurmaCheia(false)}>
                <p>A turma selecionada não possui mais vagas disponíveis.</p>
            </Dialog>

            {/* Comprovante */}
            <Dialog
                visible={showComprovante}
                header="Comprovante de Matrícula"
                onHide={() => setShowComprovante(false)}
                footer={<Button label="Imprimir" icon="pi pi-print" onClick={handlePrint} />}
            >
                <div id="comprovante-matricula">
                    <div className="header">
                        <img src={logo} alt="Conservatório" />
                        <h3>Comprovante de Matrícula – {anoAtual}</h3>
                    </div>

                    <div className="field"><strong>Aluno:</strong> {dadosComprovante?.nome || '—'}</div>
                    <div className="field"><strong>CPF:</strong> {dadosComprovante?.cpf || '—'}</div>
                    <div className="field"><strong>Data:</strong> {dadosComprovante?.data || new Date().toLocaleString('pt-BR')}</div>

                    <div className="field">
                        <strong>Turma de Teoria:</strong> {dadosComprovante?.turmaTeoria || '—'}
                    </div>

                    <div className="field">
                        <strong>Instrumento solicitado:</strong> {dadosComprovante?.instrumento || '—'}
                    </div>

                    <div className="field"><strong>Código da matrícula:</strong> {dadosComprovante?.codigo || '—'}</div>
                </div>
            </Dialog>
        </div>
    );
};